import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * T-0093 / T-0055 — citation source resolver client.
 *
 * Surface (from the ticket spec, pattern 4 in
 * `design_handoff_caliber_v2/README.md` §8 "Citation round-trip"):
 *
 *   GET /api/v1/engagements/:engagement_id/citations/:citation_id/source
 *     → 200 {
 *         document_id?,
 *         document_title,
 *         section?,
 *         page?,
 *         cell?,
 *         full_passage,
 *         cited_span_start,
 *         cited_span_end,
 *       }
 *     → 404 — citation missing OR off-engagement OR source doc deleted.
 *             The endpoint collapses all three into the same 404
 *             (existence-leak protection per T-0055).
 *
 * Backend status: shipped in PR #132 (T-0055 / C20-89). The modal at
 * `apps/web/src/components/modals/citation-source-modal.tsx` consumes
 * this client end-to-end. The retry-disabled config is intentional —
 * 404 is a meaningful "broken citation" state, not a transient error.
 */

/**
 * T-0125 added `render_hint` so the modal can pick the right inline
 * renderer per citation kind: paginated PDF, markdown section, xlsx
 * cell, or docx paragraph. Derived server-side from
 * `classification.mime_type` (cached post-T-0120) with a `null` fallback
 * for legacy citations whose source mime is unknown.
 */
export const CITATION_RENDER_HINTS = [
  'pdf_page',
  'markdown_section',
  'xlsx_cell',
  'docx_paragraph',
] as const;
export type CitationRenderHint = (typeof CITATION_RENDER_HINTS)[number];

export const CitationSourceSchema = z.object({
  /**
   * Optional document id — when present, the modal renders an
   * "Open document" deep link to /engagements/:id/documents/:doc.
   * Absent on legacy citations that don't carry the FK; the modal
   * degrades to "Copy citation" only in that case.
   */
  document_id: z.string().uuid().optional(),
  document_title: z.string(),
  section: z.string().nullable().optional(),
  page: z.number().int().nullable().optional(),
  cell: z.string().nullable().optional(),
  full_passage: z.string(),
  cited_span_start: z.number().int().nonnegative(),
  cited_span_end: z.number().int().nonnegative(),
  /** T-0125 — render_hint switches the inline viewer. `null` for legacy
   *  citations without a resolvable mime type. */
  render_hint: z.enum(CITATION_RENDER_HINTS).nullable().optional(),
});
export type CitationSource = z.infer<typeof CitationSourceSchema>;

export function getCitationSource(
  engagementId: string,
  citationId: string,
): Promise<CitationSource> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/citations/${encodeURIComponent(
      citationId,
    )}/source`,
    CitationSourceSchema,
  );
}

export interface UseCitationSourceQueryOptions {
  /** Skip the network call when false — used to gate the fetch on modal open. */
  enabled?: boolean;
}

/**
 * React Query hook for the citation-source endpoint.
 *
 *   `enabled` defaults to `Boolean(engagementId && citationId)` so the
 *   caller only needs to pass `{ enabled: isModalOpen }` to defer the
 *   fetch until the user clicks a chip. Network failures (including
 *   404 → "broken citation") surface via the standard `.error` channel.
 */
export function useCitationSourceQuery(
  engagementId: string | undefined,
  citationId: string | undefined,
  opts: UseCitationSourceQueryOptions = {},
) {
  const enabledGate = Boolean(engagementId && citationId);
  return useQuery({
    queryKey: ['citation-source', engagementId, citationId] as const,
    queryFn: () => getCitationSource(engagementId!, citationId!),
    enabled: enabledGate && (opts.enabled ?? true),
    // The source passage is immutable per-citation_id (the underlying
    // document either still exists or doesn't); cache aggressively so
    // re-opening the same chip doesn't re-fetch.
    staleTime: 5 * 60 * 1000,
    // 404 is a meaningful "broken citation" state, not a transient
    // error — surface it immediately, don't retry.
    retry: false,
  });
}
